import {
  alpha,
  Box,
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningAmberIcon from '@material-ui/icons/Warning';
import { useFormik } from 'formik';

import * as validators from 'utils/validators';

import DialogForm from './DialogForm';
import DialogHeader from './DialogHeader';

const useStyles = makeStyles(theme => ({
  description: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  rulesCard: {
    backgroundColor: alpha(theme.palette.warning.main, 0.35),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  ruleTitle: {
    display: 'flex',
    alignItems: 'center',
    color: darken(theme.palette.warning.main, 0.2),
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: '1.2rem',
    },
  },
  ruleText: {
    color: theme.palette.getContrastText(theme.palette.warning.main),
  },
  helperBox: {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: theme.spacing(1),
    color: theme.palette.info.main,
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: '1rem',
      marginTop: '2px',
    },
  },
}));

interface ModalEnviarMensagemTemplateProps {
  open: boolean;
  onClose: () => void;
  onSendInstagramTagMessage: (
    value: string,
    callback: (isSuccess: boolean) => void,
  ) => void;
}

export default function ModalEnviarMensagemDeTag({
  open,
  onClose,
  onSendInstagramTagMessage,
}: ModalEnviarMensagemTemplateProps) {
  const classes = useStyles();

  const formik = useFormik({
    initialValues: {
      mensagem: '',
    },
    validationSchema: validators.mensagemDeTagInstagramSchema,
    onSubmit: async values => {
      const { mensagem } = values;

      onSendInstagramTagMessage(mensagem, () => {
        formik.resetForm();
        onClose();
      });
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogHeader title="Enviar mensagem de Tag" onClose={handleCancel} />

      <DialogForm onSubmit={formik.handleSubmit} loading={formik.isSubmitting}>
        <DialogContent style={{ paddingTop: 0 }}>
          <Typography variant="body2" className={classes.description}>
            Esta função permite enviar uma mensagem para retomar o contato com o
            cliente. O chat será reativado para conversas livres assim que o
            cliente responder.
          </Typography>

          <Box className={classes.rulesCard}>
            <Typography variant="body2" className={classes.ruleTitle}>
              <WarningAmberIcon /> Regras de Envio da Meta
            </Typography>
            <Typography
              variant="body2"
              component="p"
              className={classes.ruleText}
            >
              • <strong>Proibido:</strong> Ofertas, cupons ou conteúdos de
              marketing.
              <br />• <strong>Permitido:</strong> Atualizações de suporte e
              resolução de problemas.
              <br />
              <br />
              <em>O uso indevido pode causar a suspensão da conta.</em>
            </Typography>
          </Box>

          <TextField
            id="mensagem"
            name="mensagem"
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Mensagem"
            variant="outlined"
            placeholder="Digite sua mensagem..."
            value={formik.values.mensagem}
            onChange={formik.handleChange}
            error={formik.touched.mensagem && Boolean(formik.errors.mensagem)}
            helperText={formik.touched.mensagem && formik.errors.mensagem}
          />

          <Box className={classes.helperBox}>
            <InfoOutlinedIcon color="action" />
            <Typography variant="caption" color="textSecondary">
              Válido apenas para clientes que interagiram nos últimos 7 dias.
              Após este prazo, o envio não será possível.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} color="default">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => formik.handleSubmit()}
            disabled={!formik.values.mensagem || !!formik.errors.mensagem}
          >
            Enviar
          </Button>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}
